import { doc, getDoc, Timestamp } from "firebase/firestore";
import { ILocation } from "../TripInfos";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { db } from "../../firebase";
import { useDisciplines, useUser } from "../../hooks";
import { useCallback } from "react";
import { isAndroid, isIOS } from "react-device-detect";
import { DOWNLOAD_URL } from "../..";

import "./event_infos.scss";
import { PrimaryButton } from "../../components";

function formatToDoubleDigit(num: number) {
  return num < 10 ? `0${num}` : num.toString();
}

interface EventSheetData {
  name: string;
  type: "contest" | "rasso" | "workshop";
  level: "local" | "region" | "national" | "international" | "mondial";
  location: ILocation;
  dates: Timestamp[];
  disciplines: string[];
  organisation?: string;
}

interface EventSheetProps {
  data: EventSheetData;
  organisation?: string;
  onClick?: () => void;
}

const eventTypes = {
  contest: "Compétition",
  rasso: "Rassemblement",
  workshop: "Workshop",
};

const eventLevels = {
  local: "Local/e",
  region: "Régional/e",
  national: "National/e",
  international: "International/e",
  mondial: "Mondial/e",
};

const EventSheet = ({ data, organisation, onClick }: EventSheetProps) => {
  const { data: disciplines } = useDisciplines();

  const organisationQuery = useUser(organisation ?? "");

  return (
    <div className="event-sheet">
      <div id="title-loc">
        <h1 id="title">{data?.name}</h1>
        <p id="loc">{data?.location.address}</p>
      </div>
      <ul id="dates">
        {data?.dates.map((date) => (
          <li key={date.toString()}>
            {[
              formatToDoubleDigit(date.toDate().getDate()),
              formatToDoubleDigit(date.toDate().getMonth() + 1),
            ].join("/")}
          </li>
        ))}
      </ul>
      <div id="labels">
        <div className="label" id="type">
          {eventTypes[data?.type]}
        </div>
        <div className="label" id="level">
          {eventLevels[data?.level]}
        </div>
      </div>
      <ul id="disciplines">
        {data?.disciplines.map((discipline) => (
          <li key={`discipline-${discipline}`} className="discipline">
            {disciplines?.[discipline]}
          </li>
        ))}
      </ul>
      {organisationQuery.data?.[0] !== undefined && (
        <div id="organisation" className="user-infos">
          <h3>Organisé par :</h3>
          <img
            id="profile-pic"
            src={organisationQuery.data?.[0].profilePic}
            alt={organisationQuery.data?.[0].username}
          />
          <p>{organisationQuery.data?.[0].username}</p>
        </div>
      )}
      <PrimaryButton onClick={onClick}>{"Y aller ->"}</PrimaryButton>
    </div>
  );
};

const EventInfos = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const nav = useNavigate();
  const location = useLocation();

  const eventQuery = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      if (!eventId) {
        nav("/");
        return undefined;
      }

      const res = await getDoc(doc(db, "Events", eventId));

      if (!res.exists()) {
        nav("/");
        return undefined;
      }

      return res.data();
    },
  });

  const openAppOrAppStore = useCallback(() => {
    if (!isIOS && !isAndroid) {
      window.open(DOWNLOAD_URL);
      return;
    }

    if (!window.open(`calshare://fr.swun.calshare${location.pathname}`)) {
      window.open(DOWNLOAD_URL);
    }
  }, [location.pathname]);

  return (
    <section className="part-page event-page">
      {eventQuery.data !== undefined && (
        <EventSheet
          data={eventQuery.data as EventSheetData}
          organisation={eventQuery.data?.organisation}
          onClick={openAppOrAppStore}
        />
      )}
      <PrimaryButton onClick={openAppOrAppStore}>Voir dans l'app</PrimaryButton>
    </section>
  );
};

export default EventInfos;
export { EventSheet, type EventSheetData };
